/**
 * Resolves and manages the active product-review provider — BazaarVoice when
 * `bazaarVoiceReviewFormConfig.enabled` is set, PowerReviews everywhere else. Loads
 * the provider script once on the client and exposes `render` (PowerReviews inline
 * widget) and `openWriteForm` (BazaarVoice hosted write-a-review lightbox).
 *
 * The BazaarVoice script URL is built dynamically from runtime config (`brand`,
 * `targetEnv`) and the current locale so a single feature-flag entry covers every
 * region and locale without a hardcoded `script` field.
 *
 * BazaarVoice locks page scroll while its lightbox is open but never restores it on
 * dismiss; this composable observes the lightbox's open shadow root and restores
 * body scroll automatically on close and on scope dispose.
 * @returns Provider flag plus `render`, `openWriteForm`, and `destroy` helpers.
 * @example
 * ```ts
 * const { useBazaarvoice, render, openWriteForm } = useReviewProvider()
 * useBazaarvoice ? openWriteForm(productId) : render('pr-write', pageId, productId)
 * ```
 */
export const useReviewProvider = () => {
  const powerReviews = usePowerReviews()
  const { bazaarVoiceReviewFormConfig } = useFeatureFlags()
  const { brand, targetEnv } = useRuntimeConfig().public
  const locale = useLocale()

  const useBazaarvoice = !!bazaarVoiceReviewFormConfig.enabled
  const [lang, region] = locale.split('-')
  const bvLocale = region ? `${lang}_${region.toUpperCase()}` : lang
  const bvEnv = targetEnv === 'PROD' ? 'production' : 'staging'
  const bvBrand = brand === 'thenorthface' ? 'TheNorthFace' : brand
  const bvScriptUrl = `https://apps.bazaarvoice.com/deployments/${bvBrand}/main_site/${bvEnv}/${bvLocale}/bv.js`

  const { load: loadBazaarvoice, remove: removeBazaarvoiceScript } = useBazaarvoice
    ? useScript(bvScriptUrl, { mode: 'client' })
    : { load: () => {}, remove: () => {} }

  const render = (containerId: string, pageId?: string, productId?: string, pageIdVariant?: string) => {
    if (useBazaarvoice && productId)
      return loadBazaarvoice()

    return powerReviews.render(containerId, pageId, pageIdVariant)
  }

  // BazaarVoice hosted write-a-review lightbox
  let observers: MutationObserver[] = []

  const observe = (target: Node, init: MutationObserverInit, callback: MutationCallback) => {
    const observer = new MutationObserver(callback)
    observer.observe(target, init)
    observers.push(observer)
    return observer
  }

  const stopObserving = () => {
    observers.forEach((observer) => observer.disconnect())
    observers = []
  }

  const restoreScroll = () => {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
  }

  const removeOverlays = () => document.querySelectorAll('[data-bv-inpagesource="BV.ui"]').forEach((overlay) => overlay.remove())

  // The lightbox is open while the host's shadow root holds a visible, non-<style>
  // content element. BazaarVoice removes or hides it on close.
  const isLightboxOpen = (host: Element) => {
    const content = host.shadowRoot && [...host.shadowRoot.children].find((el) => el.tagName !== 'STYLE')
    return !!content && getComputedStyle(content).display !== 'none'
  }

  const watchForClose = (host: Element) => {
    // The shadow root attaches asynchronously — wait for it before observing inside.
    if (!host.shadowRoot) {
      observe(host, { childList: true }, () => {
        if (host.shadowRoot) {
          stopObserving()
          watchForClose(host)
        }
      })
      return
    }

    observe(host.shadowRoot, { attributes: true, childList: true, subtree: true }, () => {
      if (!isLightboxOpen(host)) {
        restoreScroll()
        stopObserving()
      }
    })
  }

  const openWriteForm = (productId: string) => {
    if (!useBazaarvoice) return

    // Reset any prior cycle so repeated opens never stack overlays or leak observers.
    stopObserving()
    removeOverlays()
    restoreScroll()

    // BazaarVoice appends the host asynchronously — capture it, then watch its shadow
    // root for the close that BazaarVoice itself never cleans up after.
    observe(document.body, { childList: true }, () => {
      const host = document.querySelector('[data-bv-inpagesource="BV.ui"]')
      if (!host) return
      stopObserving()
      watchForClose(host)
    })

    window.BV?.ui('rr', 'submit_review', { productId })
  }

  const destroy = () => {
    powerReviews.destroy()
    stopObserving()
    removeOverlays()
    restoreScroll()
    removeBazaarvoiceScript()
  }

  return {
    useBazaarvoice,
    render,
    openWriteForm,
    destroy
  }
}
